// src/pages/locations/LocationsPage.tsx
import React from "react";
import { Navigate, useParams } from "react-router-dom";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import TabBar from "@/components/shared/ui/TabBar";

import CountriesTab from "./tabs/CountriesTab";
import RegionsTab from "./tabs/RegionsTab";
import CitiesTab from "./tabs/CitiesTab";

import { useLocationScope } from "./useLocationScope";
import { useConsumeFlash } from "./flash";
import { FlashBanner } from "./components/FlashBanner";
import { LocationsToolbar } from "./components/LocationsToolbar";
import {
  allowedLocationTabs,
  isLocationEntity,
  locationTabPath,
  type LocationEntity,
} from "./locations.access";

const TAB_CONTENT: Record<LocationEntity, React.FC> = {
  countries: CountriesTab,
  regions: RegionsTab,
  cities: CitiesTab,
};

/**
 * Countries, Regions and Cities are three views of the same dataset, so they
 * live on one page as tabs rather than three sidebar entries. Each tab keeps
 * its own URL, so links stay shareable and permissions stay per-tab.
 */
const LocationsPage: React.FC = () => {
  const { entity } = useParams<{ entity?: string }>();
  const { country } = useLocationScope();
  const flash = useConsumeFlash();

  const tabs = React.useMemo(() => allowedLocationTabs(), []);

  // No locations permission at all — the sidebar hides the entry, this covers
  // a typed URL.
  if (tabs.length === 0) return <Navigate to="/403" replace />;

  const active = isLocationEntity(entity)
    ? tabs.find((t) => t.key === entity)
    : undefined;

  // Unknown tab, or a tab this user may not see: fall back to their first one.
  if (!active) {
    return <Navigate to={locationTabPath(tabs[0].key, { country })} replace />;
  }

  const ActiveTab = TAB_CONTENT[active.key];

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Locations</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <LocationsToolbar entity={active.key} />
      </div>

      <TabBar
        ariaLabel="Locations"
        className="mb-5"
        activeKey={active.key}
        items={tabs.map((tab) => ({
          key: tab.key,
          label: tab.label,
          to: locationTabPath(tab.key, { country }),
        }))}
      />

      <FlashBanner message={flash} />

      <ActiveTab />
    </AdminLayout>
  );
};

export default LocationsPage;
